import { auth } from '../config/firebase'

const API_BASE = import.meta.env.VITE_API_URL || ''

/**
 * Get the current user's auth token
 */
async function getAuthToken() {
  const user = auth.currentUser
  if (!user) {
    throw new Error('User not authenticated')
  }
  return user.getIdToken()
}

/**
 * Fetch all prices from Stripe
 */
export async function fetchPrices() {
  const response = await fetch(`${API_BASE}/api/prices`)
  
  if (!response.ok) {
    throw new Error('Failed to fetch prices')
  }
  
  return response.json()
}

/**
 * Create a checkout session and redirect to Stripe
 * @param {string} priceId - The Stripe price ID
 * @param {string} mode - 'subscription' or 'payment'
 */
export async function createCheckoutSession(priceId, mode = 'subscription') {
  const token = await getAuthToken()
  
  const response = await fetch(`${API_BASE}/api/checkout`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ priceId, mode }),
  })
  
  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to create checkout session')
  }
  
  return response.json()
}

/**
 * Redirect to Stripe Checkout
 * @param {string} priceId - The Stripe price ID
 * @param {string} mode - 'subscription' or 'payment'
 */
export async function redirectToCheckout(priceId, mode = 'subscription') {
  const { url } = await createCheckoutSession(priceId, mode)
  
  if (url) {
    window.location.href = url
  } else {
    throw new Error('No checkout URL returned')
  }
}

/**
 * Create a customer portal session and redirect
 */
export async function openCustomerPortal() {
  const token = await getAuthToken()
  
  const response = await fetch(`${API_BASE}/api/portal`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  })
  
  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to create portal session')
  }
  
  const { url } = await response.json()
  
  if (url) {
    window.location.href = url
  } else {
    throw new Error('No portal URL returned')
  }
}

/**
 * Subscribe to a plan
 * @param {string} planId - 'basic' or 'pro'
 */
export async function subscribeToPlan(planId) {
  const priceIds = {
    basic: import.meta.env.VITE_STRIPE_PRICE_BASIC,
    pro: import.meta.env.VITE_STRIPE_PRICE_PRO,
  }
  
  const priceId = priceIds[planId]
  if (!priceId) {
    throw new Error(`Invalid plan: ${planId}`)
  }
  
  return redirectToCheckout(priceId, 'subscription')
}

/**
 * Purchase a credit pack
 * @param {string} packId - Credit pack identifier (e.g., 'credits100')
 */
export async function purchaseCreditPack(packId) {
  const priceIds = {
    credits100: import.meta.env.VITE_STRIPE_PRICE_CREDITS_100,
    credits500: import.meta.env.VITE_STRIPE_PRICE_CREDITS_500,
    credits1500: import.meta.env.VITE_STRIPE_PRICE_CREDITS_1500,
    credits5000: import.meta.env.VITE_STRIPE_PRICE_CREDITS_5000,
  }
  
  const priceId = priceIds[packId]
  if (!priceId) {
    throw new Error(`Invalid credit pack: ${packId}`)
  }
  
  return redirectToCheckout(priceId, 'payment')
}
